import { Fragment, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { RotateCcw } from 'lucide-react';
import { WEBHOOK_STATUSES, getWebhookDelivery, listWebhookDeliveries, replayWebhookDelivery } from '@mkcore/platform/schema';
import { Badge, Button, EmptyState, Field, Mono, NativeSelect, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useActionMutation } from '@/lib/mutation';
import { useCan } from '@/lib/session';

type Status = (typeof WEBHOOK_STATUSES)[number];
const search = z.object({ source: z.string().optional(), status: z.enum(WEBHOOK_STATUSES).optional() });

export const Route = createFileRoute('/_app/admin/webhooks')({ validateSearch: search, component: Webhooks });

const deliveriesQuery = (q: { source?: string; status?: Status }) =>
  infiniteQueryOptions({
    queryKey: ['admin', 'webhook', 'list', q],
    queryFn: ({ pageParam }) => call(listWebhookDeliveries, { ...(q.source && { source: q.source }), ...(q.status && { status: [q.status] }), ...(pageParam && { cursor: pageParam }) }),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: 10_000,
  });
const deliveryQuery = (id: string) => queryOptions({ queryKey: ['admin', 'webhook', id], queryFn: () => call(getWebhookDelivery, { id }) });

const tone = (s: Status) => (s === 'processed' ? 'success' : s === 'failed' || s === 'rejected' ? 'danger' : s === 'ignored' ? 'outline' : 'info');

/** The receipt log of incoming webhooks (CO-0125, CO-0967): status, error, headers and payload, and a replay. */
function Webhooks() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const q = useInfiniteQuery(deliveriesQuery(s));
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  const [open, setOpen] = useState<string | null>(null);
  const set = (patch: Partial<z.infer<typeof search>>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  return (
    <>
      <PageHeader eyebrow={t('nav.admin')} title={t('webhooks.title')} description={t('webhooks.description')} />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('webhooks.source')} className="w-40">
          {(p) => (
            <NativeSelect {...p} value={s.source ?? ''} onChange={(e) => set({ source: e.target.value || undefined })}>
              <option value="">{t('common.all')}</option>
              <option value="forgejo">Forgejo</option>
              <option value="graph">Graph</option>
            </NativeSelect>
          )}
        </Field>
        <Field label={t('common.status')} className="w-44">
          {(p) => (
            <NativeSelect {...p} value={s.status ?? ''} onChange={(e) => set({ status: (e.target.value || undefined) as Status | undefined })}>
              <option value="">{t('common.all')}</option>
              {WEBHOOK_STATUSES.map((st) => (
                <option key={st} value={st}>
                  {t(`webhooks.status.${st}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
      </div>
      {q.isLoading ? (
        <Skeleton className="h-64" />
      ) : !rows.length ? (
        <EmptyState title={t('webhooks.empty')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('webhooks.received')}</Th>
              <Th>{t('webhooks.source')}</Th>
              <Th>{t('webhooks.event')}</Th>
              <Th>{t('common.status')}</Th>
              <Th className="text-right">{t('webhooks.attempts')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((d) => (
              <Fragment key={d.id}>
                <Tr className="cursor-pointer" onClick={() => setOpen(open === d.id ? null : d.id)} aria-expanded={open === d.id}>
                  <Td className="whitespace-nowrap tabular text-fg-2">{formatDateTime(d.receivedAt)}</Td>
                  <Td>
                    {d.source} <span className="text-xs text-fg-3">{d.key}</span>
                  </Td>
                  <Td>
                    <Mono className="text-xs">{d.event}</Mono>
                  </Td>
                  <Td>
                    <Badge tone={tone(d.status)}>{t(`webhooks.status.${d.status}`)}</Badge>
                    {d.error && <div className="mt-1 max-w-md truncate text-xs text-danger">{d.error}</div>}
                  </Td>
                  <Td className="text-right tabular">{d.attempts}</Td>
                </Tr>
                {open === d.id && (
                  <Tr>
                    <Td colSpan={5}>
                      <Detail id={d.id} />
                    </Td>
                  </Tr>
                )}
              </Fragment>
            ))}
          </TBody>
        </Table>
      )}
      {q.hasNextPage && (
        <div className="mt-3 flex justify-center">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
    </>
  );
}

function Json({ value }: { value: unknown }) {
  return <pre className="max-h-64 overflow-auto rounded-md bg-mono p-3 font-mono text-xs">{JSON.stringify(value, null, 2)}</pre>;
}

function Detail({ id }: { id: string }) {
  const { t } = useTranslation();
  const canUpdate = useCan('admin:update');
  const q = useQuery(deliveryQuery(id));
  const replay = useActionMutation(replayWebhookDelivery, { invalidate: [['admin', 'webhook']], success: t('webhooks.replayed') });
  if (!q.data) return <Skeleton className="h-32" />;
  const d = q.data;
  return (
    <div className="grid gap-3 py-2">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span>
          {t('webhooks.delivery')}: <Mono className="text-xs">{d.deliveryId}</Mono>
        </span>
        {d.workflowId && (
          <span>
            {t('webhooks.job')}: <Mono className="text-xs">{d.workflowId}</Mono>
          </span>
        )}
        {canUpdate && d.status !== 'queued' && d.status !== 'rejected' && (
          <Button variant="secondary" loading={replay.isPending} onClick={() => replay.mutate({ input: { id } })}>
            <RotateCcw className="size-4" aria-hidden /> {t('webhooks.replay')}
          </Button>
        )}
      </div>
      {d.error && (
        <p role="alert" className="text-sm text-danger">
          {d.error}
        </p>
      )}
      <div className="grid gap-3 lg:grid-cols-3">
        <div>
          <h3 className="mb-1 text-xs font-bold uppercase text-fg-3">{t('webhooks.result')}</h3>
          <Json value={d.result} />
        </div>
        <div>
          <h3 className="mb-1 text-xs font-bold uppercase text-fg-3">{t('webhooks.payload')}</h3>
          <Json value={d.payload} />
        </div>
        <div>
          <h3 className="mb-1 text-xs font-bold uppercase text-fg-3">{t('webhooks.headers')}</h3>
          <Json value={d.headers} />
        </div>
      </div>
    </div>
  );
}
